import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaTimes, FaHeart, FaShoppingCart, FaStar,
  FaPlus, FaMinus, FaGem, FaShieldAlt, FaTruck,
  FaRegHeart, FaShareAlt
} from 'react-icons/fa';
import { useCart } from '../../context/CartContext';
import { useTheme } from '../../context/ThemeContext';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';

const QuickViewModal = ({ show, onHide, product }) => {
  const { addToCart, addToWishlist, removeFromWishlist, isInWishlist } = useCart();
  const { isDarkMode } = useTheme();
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState('');

  if (!product) return null;

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(price);
  };

  const handleAddToCart = () => {
    if (product.sizes && !selectedSize) {
      toast.warning('Please select a size');
      return;
    }
    addToCart({ ...product, size: selectedSize }, quantity);
    onHide();
  };

  const handleWishlistToggle = () => {
    if (isInWishlist(product.id)) {
      removeFromWishlist(product.id);
    } else {
      addToWishlist(product);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: product.name,
        text: `Check out this beautiful ${product.name}`,
        url: window.location.href
      });
    } else {
      toast.info('Link copied to clipboard!');
    }
  };

  const images = product.images || [product.image];
  const discountPercentage = product.discount ||
    (product.originalPrice && product.price
      ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
      : 0);

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      className="quick-view-modal"
      style={{ zIndex: 9999 }}>

      <Modal.Body className="p-0" style={{
        background: isDarkMode ? 'var(--bg-secondary)' : 'white',
        borderRadius: '20px'
      }}>
        <button
          onClick={onHide}
          className="btn position-absolute"
          style={{
            top: '20px',
            right: '20px',
            zIndex: 10,
            background: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
          <FaTimes />
        </button>

        <div className="row g-0">
          {/* Product Images */}
          <div className="col-lg-6 p-4">
            <div className="position-relative">
              {/* Main Image */}
              <div className="main-image mb-3" style={{
                height: '500px',
                borderRadius: '15px',
                overflow: 'hidden',
                background: '#f5f5f5'
              }}>
                <img
                  src={images[selectedImage]}
                  alt={product.name}
                  className="w-100 h-100 object-fit-cover"
                />
                {discountPercentage > 0 && (
                  <span className="position-absolute top-0 start-0 m-3 badge"
                    style={{
                      background: 'var(--gradient-maroon)',
                      padding: '8px 15px',
                      borderRadius: '20px'
                    }}>
                    {discountPercentage}% OFF
                  </span>
                )}
              </div>

              {/* Thumbnail Images */}
              {images.length > 1 && (
                <div className="d-flex gap-2 justify-content-center">
                  {images.map((img, index) => (
                    <div
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      style={{
                        width: '80px',
                        height: '80px',
                        borderRadius: '10px',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        border: selectedImage === index
                          ? '2px solid var(--primary-maroon)'
                          : '2px solid transparent',
                        transition: 'all 0.3s ease'
                      }}>
                      <img
                        src={img}
                        alt={`${product.name} ${index + 1}`}
                        className="w-100 h-100 object-fit-cover"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Product Details */}
          <div className="col-lg-6 p-4">
            <div className="product-details">
              {/* Category & Collection */}
              <div className="d-flex gap-2 mb-2">
                <span className="badge" style={{
                  background: 'rgba(128, 0, 32, 0.1)',
                  color: 'var(--primary-maroon)',
                  padding: '5px 12px',
                  borderRadius: '15px'
                }}>
                  {product.category}
                </span>
                {product.collection && (
                  <span className="badge" style={{
                    background: 'rgba(212, 175, 55, 0.1)',
                    color: 'var(--accent-gold)',
                    padding: '5px 12px',
                    borderRadius: '15px'
                  }}>
                    {product.collection}
                  </span>
                )}
              </div>

              {/* Product Name */}
              <h3 className="mb-3" style={{
                fontFamily: 'var(--font-secondary)',
                fontWeight: '700'
              }}>
                {product.name}
              </h3>

              {/* Rating */}
              <div className="d-flex align-items-center mb-3">
                <div className="d-flex" style={{ color: 'var(--accent-gold)' }}>
                  {[...Array(5)].map((_, i) => (
                    <FaStar
                      key={i}
                      style={{
                        fontSize: '16px',
                        opacity: i < Math.floor(product.rating || 4.5) ? 1 : 0.3
                      }}
                    />
                  ))}
                </div>
                <span className="ms-2">
                  {product.rating || 4.5} ({product.reviews || 0} reviews)
                </span>
              </div>

              {/* Price */}
              <div className="mb-4">
                <div className="d-flex align-items-baseline gap-3">
                  <h3 className="mb-0" style={{
                    color: 'var(--primary-maroon)',
                    fontWeight: '700'
                  }}>
                    {formatPrice(product.price)}
                  </h3>
                  {product.originalPrice && product.originalPrice > product.price && (
                    <h5 className="text-muted text-decoration-line-through">
                      {formatPrice(product.originalPrice)}
                    </h5>
                  )}
                </div>
              </div>

              {/* Description */}
              <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
                {product.description || 'Exquisite piece crafted with precision and elegance.'}
              </p>

              {/* Product Specs */}
              <div className="specs mb-4">
                <h6 className="mb-3">Product Details</h6>
                <div className="row g-3">
                  {product.weight && (
                    <div className="col-6">
                      <small className="text-muted">Weight</small>
                      <p className="mb-0 fw-bold">{product.weight}</p>
                    </div>
                  )}
                  {product.purity && (
                    <div className="col-6">
                      <small className="text-muted">Purity</small>
                      <p className="mb-0 fw-bold">{product.purity}</p>
                    </div>
                  )}
                  {product.carat && (
                    <div className="col-6">
                      <small className="text-muted">Carat</small>
                      <p className="mb-0 fw-bold">{product.carat}</p>
                    </div>
                  )}
                  {product.clarity && (
                    <div className="col-6">
                      <small className="text-muted">Clarity</small>
                      <p className="mb-0 fw-bold">{product.clarity}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Size Selection */}
              {product.sizes && (
                <div className="mb-4">
                  <h6 className="mb-3">Select Size</h6>
                  <div className="d-flex gap-2">
                    {product.sizes.map((size) => (
                      <button
                        key={size}
                        onClick={() => setSelectedSize(size)}
                        className="btn"
                        style={{
                          border: selectedSize === size
                            ? '2px solid var(--primary-maroon)'
                            : '1px solid var(--border-color)',
                          borderRadius: '10px',
                          padding: '8px 16px',
                          background: selectedSize === size
                            ? 'var(--primary-maroon)'
                            : 'transparent',
                          color: selectedSize === size
                            ? 'white'
                            : 'var(--text-primary)'
                        }}>
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Quantity Selector */}
              <div className="mb-4">
                <h6 className="mb-3">Quantity</h6>
                <div className="d-flex align-items-center gap-3">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="btn"
                    style={{
                      background: 'var(--bg-tertiary)',
                      border: '1px solid var(--border-color)',
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px'
                    }}>
                    <FaMinus />
                  </button>
                  <span style={{
                    minWidth: '50px',
                    textAlign: 'center',
                    fontSize: '18px',
                    fontWeight: '600'
                  }}>
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="btn"
                    style={{
                      background: 'var(--bg-tertiary)',
                      border: '1px solid var(--border-color)',
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px'
                    }}>
                    <FaPlus />
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="d-flex gap-3 mb-4">
                <button
                  onClick={handleAddToCart}
                  className="btn flex-grow-1"
                  style={{
                    background: 'var(--gradient-maroon)',
                    color: 'white',
                    padding: '12px',
                    borderRadius: '15px',
                    fontWeight: '600'
                  }}>
                  <FaShoppingCart className="me-2" />
                  Add to Cart
                </button>
                <button
                  onClick={handleWishlistToggle}
                  className="btn"
                  style={{
                    background: isInWishlist(product.id)
                      ? 'var(--primary-maroon)'
                      : 'var(--bg-tertiary)',
                    color: isInWishlist(product.id)
                      ? 'white'
                      : 'var(--text-primary)',
                    padding: '12px',
                    borderRadius: '15px',
                    width: '50px'
                  }}>
                  {isInWishlist(product.id) ? <FaHeart /> : <FaRegHeart />}
                </button>
                <button
                  onClick={handleShare}
                  className="btn"
                  style={{
                    background: 'var(--bg-tertiary)',
                    padding: '12px',
                    borderRadius: '15px',
                    width: '50px'
                  }}>
                  <FaShareAlt />
                </button>
              </div>

              {/* Features */}
              <div className="features">
                <div className="d-flex gap-4">
                  <div className="d-flex align-items-center">
                    <FaShieldAlt className="me-2" style={{ color: 'var(--primary-maroon)' }} />
                    <small>100% Certified</small>
                  </div>
                  <div className="d-flex align-items-center">
                    <FaTruck className="me-2" style={{ color: 'var(--primary-maroon)' }} />
                    <small>Free Shipping</small>
                  </div>
                  <div className="d-flex align-items-center">
                    <FaGem className="me-2" style={{ color: 'var(--primary-maroon)' }} />
                    <small>Lifetime Exchange</small>
                  </div>
                </div>
              </div>

              {/* View Full Details Link */}
              <div className="mt-4">
                <Link
                  to={`/product/${product.id}`}
                  onClick={onHide}
                  style={{
                    color: 'var(--primary-maroon)',
                    textDecoration: 'none',
                    fontWeight: '600'
                  }}>
                  View Full Product Details →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default QuickViewModal;